import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { isEmpty } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, Outlet, To, useNavigate, useSearchParams } from 'react-router-dom';

import { useDeleteUsersMutation, useGetUsersQuery } from '@/api/v1';
import EditIcon from '@/assets/icons/pencil.svg?react';
import { EmptyTableView } from '@/components/atoms/EmptyTableView';
import { ConfirmationModal } from '@/components/molecules/ConfirmationModal';
import { FullScreenLoader } from '@/components/molecules/FullScreenLoader';
import { UserStatusCell } from '@/components/molecules/UserStatusCell';
import departments from '@/data/departments.json';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { useTypedSearchParams } from '@/hooks/useTypedSearchParams';
import { authSelector } from '@/StateManagement/AuthReducer';
import { LabelAndValue } from '@/types/labelAndValue';
import { GetUsersFilters, User } from '@/types/user';

interface ModalUrls {
  readonly new: To;
  readonly roles: To;
}

export const SettingsUsersTab: React.FC = (): React.ReactElement => {
  const [search] = useSearchParams();
  const toast = useToast();
  const tr = useTranslator();
  const navigate = useNavigate();

  const [filters, setFilters] = useTypedSearchParams<GetUsersFilters>(GetUsersFilters.initial());
  const [deleteUsers] = useDeleteUsersMutation();
  const [selection, setSelection] = React.useState<Record<string, boolean>>({});
  const [wantsRemove, setWantsRemove] = React.useState<boolean>(false);

  const { user } = useSelector(authSelector);

  const {
    data: { content: users, totalPages, totalElements, pageable: { pageNumber } } = {
      content: [],
      totalElements: 0,
      totalPages: 1,
      pageable: { pageNumber: 0 },
    },
    isLoading,
    error,
  } = useGetUsersQuery(filters);

  const handlePageChange = React.useCallback(
    (_: React.ChangeEvent<unknown>, value: number): void => {
      setFilters(
        (filters: GetUsersFilters): GetUsersFilters => ({
          ...filters,
          page: value,
        }),
      );
    },
    [setFilters],
  );

  const onCloseModal = React.useCallback((): void => {
    navigate({
      pathname: '.',
      search: search.toString(),
    });
  }, [navigate, search]);

  const onRowSelected = React.useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const { checked, value } = event.target;

    setSelection((selection: Record<string, boolean>): Record<string, boolean> => {
      if (checked) {
        return {
          ...selection,
          [value]: true,
        };
      } else {
        const { [value]: _, ...rest } = selection;
        return rest;
      }
    });
  }, []);

  const onRemoveSelection = React.useCallback((): void => {
    setWantsRemove(!isEmpty(selection));
  }, [selection]);

  const onConfirmRemoveUsers = React.useCallback(async (): Promise<void> => {
    const selectedUserIds = Object.keys(selection).filter(
      (userId: string): boolean => selection[userId] && userId !== user?.id,
    );

    if (selectedUserIds.length === 0) {
      toast.show('No users selected for deletion', 'error');
      return;
    }

    try {
      // Delete users one by one since the API expects individual user IDs
      const deletePromises = selectedUserIds.map(
        (userId: string): Promise<void> => deleteUsers(userId).unwrap(),
      );
      await Promise.all(deletePromises);

      toast.show(
        `Successfully removed ${selectedUserIds.length} user${selectedUserIds.length > 1 ? 's' : ''}`,
        'success',
      );
      setSelection({});
      onCloseModal();
    } catch (error) {
      console.error('Error deleting users:', error);
      toast.show('Error removing users', 'error');
    }
  }, [deleteUsers, selection, toast, onCloseModal, user?.id]);

  const onCancelRemoveUsers = React.useCallback((): void => {
    setWantsRemove(false);
  }, []);

  const selectedUserCount = React.useMemo(
    (): number =>
      Object.keys(selection).filter((userId: string): boolean => selection[userId]).length,
    [selection],
  );

  const modalTitle = React.useMemo(
    (): string =>
      tr(
        'ARE_YOU_SURE_YOU_WANT_TO_REMOVE_THESE_USERS_FROM_YOUR_TEAM',
        selectedUserCount > 1 ? tr('THESE_USERS') : tr('THIS_USER'),
      ),
    [selectedUserCount, tr],
  );

  const modalButtonText = React.useMemo(
    (): string => tr('REMOVE_USERS_BUTTON_LABEL', selectedUserCount > 1 ? tr('USERS') : tr('USER')),
    [selectedUserCount, tr],
  );

  const departmentNames = React.useMemo((): Record<string, string> => {
    return departments.reduce(
      (record: Record<string, string>, value: LabelAndValue): Record<string, string> => {
        return { ...record, [value.value]: tr(value.label) };
      },
      {},
    );
  }, [tr]);

  const getDepartmentName = React.useCallback(
    (key: string | undefined): string | null => {
      if (!key) {
        return null;
      }
      return departmentNames[key] ?? null;
    },
    [departmentNames],
  );

  const urls = React.useMemo(
    (): ModalUrls => ({
      roles: { pathname: 'roles', search: search.toString() },
      new: { pathname: 'new', search: search.toString() },
    }),
    [search],
  );

  return (
    <Box sx={styles.container}>
      <Box display="flex" alignItems="center" width="100%" gap={1}>
        <Button variant="contained" size="compact" component={Link} to={urls.new}>
          {tr('ADD_NEW_USER')}
        </Button>
        <Button variant="light" size="compact" component={Link} to={urls.roles}>
          {tr('MANAGE_ROLES')}
        </Button>
        <Box ml="auto">
          <Button
            variant="outlined"
            color="error"
            size="compact"
            disabled={isEmpty(selection)}
            onClick={onRemoveSelection}>
            {tr('REMOVE')}
          </Button>
        </Box>
      </Box>

      <Paper elevation={3} sx={styles.paper}>
        <Box sx={styles.tableBox}>
          {isLoading ? (
            <Box display="relative" width="100%" height="100%">
              <FullScreenLoader loading={true} />
            </Box>
          ) : error ? (
            <EmptyTableView title="No users to show" message="There was an error fetching users" />
          ) : (
            <Table sx={styles.table}>
              <colgroup>
                <col width="69px" />
                <col width="30%" />
                <col width="20%" />
                <col width="22%" />
                <col width="18%" />
                <col width="80px" />
              </colgroup>
              <TableHead sx={styles.tableHeader}>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>{tr('NAME')}</TableCell>
                  <TableCell>{tr('ROLE')}</TableCell>
                  <TableCell>{tr('STATUS')}</TableCell>
                  <TableCell>{tr('DEPARTMENT')}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map(
                  (tableUser: User): React.ReactElement => (
                    <TableRow key={tableUser.id}>
                      <TableCell align="right">
                        <Box mx={-1}>
                          {tableUser.id !== user?.id && (
                            <Checkbox
                              size="small"
                              checked={Boolean(selection[tableUser.id])}
                              value={tableUser.id}
                              onChange={onRowSelected}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box>
                            <Typography variant="body1">
                              {tableUser.firstName} {tableUser.lastName}
                              {tableUser.id === user?.id && (
                                <Typography
                                  component="span"
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ ml: 1 }}>
                                  ({tr('YOU')})
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{!tableUser.group ? '--' : tr(tableUser.group)}</TableCell>
                      <TableCell>
                        <UserStatusCell user={tableUser} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          {getDepartmentName(tableUser.department) ?? '--'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton color="inherit" component={Link} to={tableUser.id}>
                          <EditIcon width={24} height={24} fill="currentColor" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ),
                )}
              </TableBody>
            </Table>
          )}
        </Box>
      </Paper>
      {totalElements > 0 && (
        <Box display="flex" alignItems="center" justifyContent="center">
          <Pagination count={totalPages} page={pageNumber + 1} onChange={handlePageChange} />
        </Box>
      )}

      <Outlet />

      <ConfirmationModal
        open={wantsRemove}
        title={modalTitle}
        description={tr('THIS_ACTION_IS_NOT_REVERSIBLE')}
        width={744}
        confirmButtonText={modalButtonText}
        abortButtonText={tr('CANCEL')}
        onConfirm={onConfirmRemoveUsers}
        onAbort={onCancelRemoveUsers}
      />
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    height: '100%',
  },
  paper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  tableBox: {
    position: 'absolute',
    inset: 0,
    overflowY: 'auto',
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
    minHeight: 0,
  },
  tableHeader: {
    position: 'sticky',
    backgroundColor: 'custom.White',
    top: 0,
    zIndex: 1,
  },
};
